import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Temporal } from 'temporal-polyfill';
import type { ClassSeries } from '../domain/types';
import { formatDuration, formatSeriesSummary } from '../domain/format';
import {
  SERIES_TABS,
  SERIES_TAB_LABELS,
  SERIES_TYPE_FILTERS,
  SERIES_TYPE_FILTER_LABELS,
  matchesSeriesView,
  type SeriesTab,
  type SeriesTypeFilter,
} from '../domain/seriesFilter';
import { useAsync, useRepository } from './repositoryContext';
import { EmptyState, ErrorBanner } from './components';
import { ClassForm } from './ClassForm';
import { ChevronRight } from './icons';

export function ClassesView(props: {
  /** Set when arriving from "Edit the whole series"; cleared once handled. */
  editSeriesId?: string | null;
  /** Set when arriving from the schedule's empty state. */
  startNew?: boolean;
  onFormClosed?: () => void;
}): JSX.Element {
  const repo = useRepository();
  const locations = useAsync(() => repo.listLocations(), []);
  const series = useAsync(() => repo.listSeries(), []);

  const [creating, setCreating] = useState(props.startNew === true);
  const [editingId, setEditingId] = useState<string | null>(props.editSeriesId ?? null);
  const [error, setError] = useState<Error | undefined>(undefined);
  // Deliberately session state, not a persisted preference: coming back to the
  // tab should always land on the classes that matter today.
  const [tab, setTab] = useState<SeriesTab>('active');
  const [typeFilter, setTypeFilter] = useState<SeriesTypeFilter>('all');

  const all = series.data ?? [];
  const today = Temporal.Now.plainDateISO().toString();
  const visible = all.filter((item) => matchesSeriesView(item, tab, typeFilter, today));
  const editing = all.find((item) => item.id === editingId);
  const byId = new Map((locations.data ?? []).map((location) => [location.id, location]));

  function closeForm(): void {
    setCreating(false);
    setEditingId(null);
    props.onFormClosed?.();
  }

  async function setActive(item: ClassSeries, active: boolean): Promise<void> {
    try {
      // Stamp when the class was archived (and clear it on restore) so stats can
      // stop counting an archived class's history at the right moment.
      await repo.updateSeries(item.id, {
        active,
        archivedAt: active ? null : Temporal.Now.instant().toString(),
      });
      series.reload();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  async function remove(item: ClassSeries): Promise<boolean> {
    if (!confirm(`Delete "${item.title}"?`)) {
      return false;
    }
    try {
      await repo.deleteSeries(item.id);
      series.reload();
      return true;
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      return false;
    }
  }

  // The form is a screen of its own, so it replaces the list rather than sitting
  // inside it — the mobile convention, and it keeps focus on one task.
  if (creating || editingId !== null) {
    // Mounting before the places have loaded would default the picker to
    // "somewhere new" even when the instructor already has places, and would
    // leave the summary without a place name.
    if (locations.data === undefined || series.data === undefined) {
      return <p class="muted">Loading…</p>;
    }
    return (
      <ClassForm
        locations={locations.data}
        series={editing}
        seriesCount={series.data.length}
        onSaved={() => {
          closeForm();
          series.reload();
          locations.reload();
        }}
        onCancel={closeForm}
        onArchiveToggle={
          editing === undefined
            ? undefined
            : async () => {
                await setActive(editing, !editing.active);
                closeForm();
              }
        }
        onDelete={
          editing === undefined
            ? undefined
            : async () => {
                const deleted = await remove(editing);
                if (deleted) {
                  closeForm();
                }
              }
        }
      />
    );
  }

  return (
    <section>
      <header class="screen-head">
        <h2>Classes</h2>
        <button
          type="button"
          class="primary"
          aria-label="Add a class"
          onClick={() => setCreating(true)}
        >
          + Add
        </button>
      </header>

      <ErrorBanner error={series.error ?? locations.error ?? error} />

      {!series.loading && all.length === 0 ? (
        <EmptyState title="No classes yet.">
          <p>Add the classes you teach and they'll show up on your schedule.</p>
          <button type="button" class="primary" onClick={() => setCreating(true)}>
            Add a class
          </button>
        </EmptyState>
      ) : null}

      {all.length > 0 ? (
        <>
          <div class="segmented" role="group" aria-label="Show classes">
            {SERIES_TABS.map((choice) => (
              <button
                key={choice}
                type="button"
                class={`segment${tab === choice ? ' selected' : ''}`}
                aria-pressed={tab === choice}
                onClick={() => setTab(choice)}
              >
                {SERIES_TAB_LABELS[choice]}
              </button>
            ))}
          </div>
          {/* The type refinement applies within whichever tab is showing, so it
              keeps its selection when the tab changes. */}
          <div class="chips" role="group" aria-label="Filter by type">
            {SERIES_TYPE_FILTERS.map((choice) => (
              <button
                key={choice}
                type="button"
                class={`chip${typeFilter === choice ? ' selected' : ''}`}
                aria-pressed={typeFilter === choice}
                onClick={() => setTypeFilter(choice)}
              >
                {SERIES_TYPE_FILTER_LABELS[choice]}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {all.length > 0 && visible.length === 0 ? (
        <p class="muted">
          No{' '}
          {(typeFilter === 'all'
            ? SERIES_TAB_LABELS[tab]
            : SERIES_TYPE_FILTER_LABELS[typeFilter]
          ).toLowerCase()}{' '}
          classes.
        </p>
      ) : null}

      <ul class="rows">
        {visible.map((item) => {
          const place = byId.get(item.locationId);
          return (
            <li key={item.id}>
              <button type="button" class="row" onClick={() => setEditingId(item.id)}>
                <span
                  class="row-bar"
                  style={{ background: item.color ?? 'var(--muted)' }}
                  aria-hidden="true"
                />
                <span class="row-what">
                  <span class="row-title">
                    {item.title}
                    {item.shared ? <span class="tag">Shared</span> : null}
                    {item.isSub ? <span class="tag">Sub</span> : null}
                    {item.active ? null : <span class="tag">Archived</span>}
                  </span>
                  <span class="row-place">
                    {formatSeriesSummary({
                      recurrence: item.recurrence,
                      startTime: item.startTime,
                      startDate: item.startDate,
                      locationName: place?.name ?? 'No place',
                    })}
                  </span>
                  <span class="row-place">{formatDuration(item.durationMinutes)}</span>
                </span>
                <ChevronRight />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
